import { Component } from '@theme/component';
import '@theme/theme-drawer';
import { getScrollContainer, getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Pattern header presentation; cart state remains owned by Horizon.
 * @typedef {object} HeaderRefs
 * @property {HTMLElement} [header]
 * @property {HTMLElement} [inner]
 * @property {HTMLElement} [progress]
 * @property {HTMLButtonElement} [burger]
 * @property {HTMLDialogElement} [drawer]
 * @extends {Component<HeaderRefs>}
 */
class PatternHeader extends Component {
  #controller;
  #frame = 0;
  #overflow;
  #scrollTarget;
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#connect();
  }

  updatedCallback() {
    this.#disconnect();
    super.updatedCallback();
    this.#connect();
  }

  disconnectedCallback() {
    this.closeMenu();
    this.#disconnect();
    super.disconnectedCallback();
  }

  #connect() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#bindScrollTarget();
    scrollContainerMediaQuery.addEventListener('change', this.#bindScrollTarget, { signal });
    window.addEventListener('resize', this.#resize, { signal });
    this.refs.drawer?.addEventListener('close', this.#menuClosed, { signal });
    document.addEventListener('pointerdown', this.#dismissDropdowns, { signal });
    document.addEventListener('focusin', this.#dismissDropdowns, { signal });
    this.addEventListener('keydown', this.#dropdownKeydown, { signal });
    this.addEventListener('toggle', this.#dropdownToggled, { capture: true, signal });
    this.addEventListener('load', this.#resize, { capture: true, signal });
    document.fonts.addEventListener('loadingdone', this.#resize, { signal });
    this.#resizeObserver = new ResizeObserver(this.#resize);
    if (this.refs.inner) this.#resizeObserver.observe(this.refs.inner);
    this.#resize();
    this.#schedule();
    this.#syncMenu();
  }

  #disconnect() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    this.#scrollTarget?.removeEventListener('scroll', this.#schedule);
    this.#scrollTarget = undefined;
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#restoreOverflow();
  }

  #bindScrollTarget = () => {
    this.#scrollTarget?.removeEventListener('scroll', this.#schedule);
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#schedule, { passive: true });
    this.#schedule();
  };

  #schedule = () => {
    if (!this.#frame) this.#frame = requestAnimationFrame(this.#render);
  };

  #resize = () => {
    const { inner, burger } = this.refs;
    // Measure the natural desktop row before deciding whether it fits. Restore
    // the collapsed state synchronously so no intermediate layout is painted.
    this.removeAttribute('data-nav-collapsed');
    let collapsed = window.innerWidth <= Number(this.dataset.collapseBreakpoint);
    if (!collapsed && inner && burger) {
      const items = [...inner.children].filter((item) => getComputedStyle(item).display !== 'none');
      const gap = Number.parseFloat(getComputedStyle(inner).columnGap) || 0;
      const needed = items.reduce((width, item) => width + item.getBoundingClientRect().width, 0) + gap * (items.length - 1);
      collapsed = needed > inner.clientWidth;
    }
    this.toggleAttribute('data-nav-collapsed', collapsed);
    if (!collapsed) this.closeMenu();
    this.#schedule();
  };

  #render = () => {
    this.#frame = 0;
    const container = getScrollContainer();
    const scrollTop = getScrollTop();
    const max = container.scrollHeight - container.clientHeight;
    if (this.refs.progress) this.refs.progress.style.width = (max > 0 ? Math.min(100, Math.max(0, scrollTop / max * 100)) : 0) + '%';
    this.refs.header?.classList.toggle('is-stuck', scrollTop > Number(this.dataset.stuckOffset));
    if (this.refs.drawer?.open) this.#positionDrawer();
  };

  #positionDrawer() {
    const bottom = this.refs.header?.getBoundingClientRect().bottom ?? 0;
    this.style.setProperty('--tb-drawer-top', `${Math.max(0, bottom)}px`);
  }

  /** Opens Horizon's existing cart drawer, retaining the link fallback before upgrade. @param {MouseEvent} event */
  openCart(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    const drawer = document.getElementById('cart-drawer');
    if (!drawer || typeof drawer.toggle !== 'function') return;
    event.preventDefault();
    this.closeMenu();
    drawer.toggle();
  }

  /** Toggles the native modal menu. */
  toggleMenu() {
    const { drawer } = this.refs;
    if (!drawer) return;
    if (drawer.open) {
      this.closeMenu();
    } else {
      this.#positionDrawer();
      drawer.showModal();
      this.#syncMenu();
    }
  }

  /** Closes the menu; the native dialog restores the trigger's focus. */
  closeMenu() {
    if (this.refs.drawer?.open) this.refs.drawer.close();
    this.#syncMenu();
  }

  /** @param {MouseEvent} event */
  handleMenuClick(event) {
    const target = event.composedPath()[0];
    if (target instanceof Element && target.closest('a[href]')) this.closeMenu();
  }

  /** @param {Event} event */
  #dismissDropdowns = (event) => {
    const target = event.composedPath()[0];
    if (!(target instanceof Node)) return;
    for (const menu of this.querySelectorAll('details[data-tb-menu][open]')) {
      if (!menu.contains(target)) menu.removeAttribute('open');
    }
  };

  /** @param {KeyboardEvent} event */
  #dropdownKeydown = (event) => {
    if (event.key !== 'Escape' || !(event.target instanceof Element)) return;
    const menu = event.target.closest('details[data-tb-menu][open]');
    if (!menu || !this.contains(menu)) return;
    event.preventDefault();
    event.stopPropagation();
    menu.removeAttribute('open');
    menu.querySelector('summary')?.focus();
  };

  /** @param {Event} event */
  #dropdownToggled = (event) => {
    const current = event.target;
    if (!(current instanceof HTMLDetailsElement) || !current.matches('[data-tb-menu]') || !current.open) return;
    for (const menu of this.querySelectorAll('details[data-tb-menu][open]')) {
      if (menu !== current) menu.removeAttribute('open');
    }
  };

  #menuClosed = () => this.#syncMenu();

  #syncMenu() {
    const open = this.refs.drawer?.open ?? false;
    this.refs.burger?.setAttribute('aria-expanded', String(open));
    this.refs.burger?.setAttribute('aria-label', (open ? this.dataset.menuCloseLabel : this.dataset.menuOpenLabel) || '');
    if (open && this.#overflow === undefined) {
      this.#overflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    } else if (!open) {
      this.#restoreOverflow();
    }
  }

  #restoreOverflow() {
    if (this.#overflow === undefined) return;
    document.body.style.overflow = this.#overflow;
    this.#overflow = undefined;
  }
}

if (!customElements.get('tb-header-component')) customElements.define('tb-header-component', PatternHeader);
